import { useTranslation } from '@gimlite/common';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Col } from '../col/col.component';
import { Row } from '../row/row.component';
import { Text } from '../text/text.component';
import { Zone, ZoneArea } from '../zone/zone.component';
import './passwordStrengthValidator.component.scss';

const SPECIAL_CHARACTERS = '!@#$%^&*.';
const SPECIAL_CHARACTERS_REGEX = new RegExp(`[${SPECIAL_CHARACTERS}]`);

export interface PasswordCriteria {
  minCharacters: boolean;
  uppercaseLetter: boolean;
  lowercaseLetter: boolean;
  digit: boolean;
  specialCharacter: boolean;
}

export interface PasswordStrength {
  entropy: number;
  level: 'very_weak' | 'weak' | 'medium' | 'strong' | 'very_strong' | null;
  percentage: number;
}

export interface PasswordValidationResult {
  criteria: PasswordCriteria;
  strength: PasswordStrength;
  isValid: boolean;
}

declare namespace PasswordStrengthValidatorType {
  type Props = {
    password: string | null | undefined;
    touched?: boolean;
    minLength?: number;
    onChange?: (result: PasswordValidationResult) => void;
    showStrengthBar?: boolean;
    showCriteria?: boolean;
  };
}

export const PasswordStrengthValidator = observer(
  ({
    password,
    touched = false,
    minLength = 12,
    onChange,
    showStrengthBar = true,
    showCriteria = true,
  }: PasswordStrengthValidatorType.Props) => {
    const { t } = useTranslation();

    const [passwordCriteria, setPasswordCriteria] = useState<PasswordCriteria>({
      minCharacters: false,
      uppercaseLetter: false,
      lowercaseLetter: false,
      digit: false,
      specialCharacter: false,
    });

    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
      entropy: 0,
      level: null,
      percentage: 0,
    });

    const validatePasswordCriteria = useCallback(
      (pwd: string | null | undefined): PasswordCriteria => {
        if (!pwd) {
          return {
            minCharacters: false,
            uppercaseLetter: false,
            lowercaseLetter: false,
            digit: false,
            specialCharacter: false,
          };
        }

        return {
          minCharacters: new RegExp(`^(?=.{${minLength},})`).test(pwd),
          uppercaseLetter: /^(?=.*[A-Z])/.test(pwd),
          lowercaseLetter: /^(?=.*[a-z])/.test(pwd),
          digit: /^(?=.*\d)/.test(pwd),
          specialCharacter: SPECIAL_CHARACTERS_REGEX.test(pwd),
        };
      },
      [minLength],
    );

    // Calcul de la force du mot de passe selon les critères ANSSI
    // Basé sur l'entropie : H = L × log2(N)
    // où L = longueur, N = taille de l'alphabet
    const calculatePasswordStrength = useCallback(
      (pwd: string | null | undefined): PasswordStrength => {
        if (!pwd || pwd.length === 0) {
          return {
            entropy: 0,
            level: null,
            percentage: 0,
          };
        }

        let alphabetSize = 0;

        // Calcul de la taille de l'alphabet selon les caractères utilisés
        if (/[a-z]/.test(pwd)) alphabetSize += 26; // Minuscules
        if (/[A-Z]/.test(pwd)) alphabetSize += 26; // Majuscules
        if (/\d/.test(pwd)) alphabetSize += 10; // Chiffres
        if (SPECIAL_CHARACTERS_REGEX.test(pwd)) alphabetSize += 32; // Caractères spéciaux

        // Calcul de l'entropie : H = L × log2(N)
        const entropy = pwd.length * Math.log2(alphabetSize);

        // Niveaux selon les recommandations ANSSI
        let level: 'very_weak' | 'weak' | 'medium' | 'strong' | 'very_strong';
        let percentage: number;

        if (entropy < 40) {
          level = 'very_weak';
          percentage = Math.min((entropy / 40) * 25, 25);
        } else if (entropy < 60) {
          level = 'weak';
          percentage = 25 + ((entropy - 40) / 20) * 25;
        } else if (entropy < 80) {
          level = 'medium';
          percentage = 50 + ((entropy - 60) / 20) * 25;
        } else if (entropy < 100) {
          level = 'strong';
          percentage = 75 + ((entropy - 80) / 20) * 15;
        } else {
          level = 'very_strong';
          percentage = Math.min(90 + ((entropy - 100) / 50) * 10, 100);
        }

        return {
          entropy: Math.round(entropy * 10) / 10,
          level,
          percentage: Math.round(percentage),
        };
      },
      [],
    );

    useEffect(() => {
      const criteria = validatePasswordCriteria(password);
      const strength = calculatePasswordStrength(password);

      setPasswordCriteria(criteria);
      setPasswordStrength(strength);

      // Vérifier si tous les critères sont remplis
      const isValid = Object.values(criteria).every((criterion) => criterion);

      // Appeler le callback si fourni
      onChange?.({ criteria, strength, isValid });
    }, [password, validatePasswordCriteria, calculatePasswordStrength, onChange]);

    const getStrengthColor = (level: PasswordStrength['level']) => {
      switch (level) {
        case 'very_weak':
          return '#EF4444';
        case 'weak':
          return '#F97316';
        case 'medium':
          return '#F59E0B';
        case 'strong':
          return '#10B981';
        case 'very_strong':
          return '#059669';
        default:
          return '#E5E7EB';
      }
    };

    const getStrengthTextColor = (
      level: PasswordStrength['level'],
    ): 'alert_error' | 'alert_warning' | 'alert_success' => {
      switch (level) {
        case 'very_weak':
          return 'alert_error';
        case 'weak':
        case 'medium':
          return 'alert_warning';
        case 'strong':
        case 'very_strong':
          return 'alert_success';
        default:
          return 'alert_error';
      }
    };

    return (
      <Col width="100%" gap={16} className="passwordStrengthValidator">
        {showStrengthBar && (
          <Col width="100%" gap={4} alignItems="flex-start">
            <Row
              width="max-content"
              justifyContent="space-between"
              gap={8}
              alignItems="center"
            >
              <span className="passwordStrengthValidator__label">
                {t('watermelon-passwordStrength')}
              </span>
              {passwordStrength.level && (
                <Text
                  variant="label"
                  whiteSpace="nowrap"
                  color={getStrengthTextColor(passwordStrength.level)}
                >
                  {passwordStrength.level === 'very_weak' &&
                    t('watermelon-veryWeak')}
                  {passwordStrength.level === 'weak' && t('watermelon-weak')}
                  {passwordStrength.level === 'medium' &&
                    t('watermelon-medium')}
                  {passwordStrength.level === 'strong' &&
                    t('watermelon-strong')}
                  {passwordStrength.level === 'very_strong' &&
                    t('watermelon-veryStrong')}
                </Text>
              )}
            </Row>

            <div className="passwordStrengthValidator__bar">
              {[1, 2, 3, 4, 5].map((segment) => {
                const isActive =
                  passwordStrength.level &&
                  ((passwordStrength.level === 'very_weak' && segment <= 1) ||
                    (passwordStrength.level === 'weak' && segment <= 2) ||
                    (passwordStrength.level === 'medium' && segment <= 3) ||
                    (passwordStrength.level === 'strong' && segment <= 4) ||
                    (passwordStrength.level === 'very_strong' && segment <= 5));

                const segmentColor = isActive
                  ? getStrengthColor(passwordStrength.level)
                  : '#E5E7EB';

                return (
                  <div
                    key={segment}
                    className="passwordStrengthValidator__bar__segment"
                    style={{ backgroundColor: segmentColor }}
                  />
                );
              })}
            </div>
          </Col>
        )}

        {showCriteria && (
          <Zone
            zones={[
              ['min_characters'],
              ['uppercase_letter'],
              ['lowercase_letter'],
              ['digit'],
              ['special_character'],
            ]}
            rows={[
              'min-content',
              'min-content',
              'min-content',
              'min-content',
              'min-content',
            ]}
            columns={['min-content']}
            gapY={0}
          >
            <ZoneArea area="min_characters">
              <Text
                whiteSpace="nowrap"
                variant="caption"
                color={
                  !touched
                    ? 'black_50'
                    : passwordCriteria.minCharacters
                      ? 'alert_success'
                      : 'alert_error'
                }
              >
                {t('watermelon-MIN_CHARACTERS').replace(
                  '[MIN]',
                  minLength.toString(),
                )}
              </Text>
            </ZoneArea>

            <ZoneArea area="uppercase_letter">
              <Text
                whiteSpace="nowrap"
                variant="caption"
                color={
                  !touched
                    ? 'black_50'
                    : passwordCriteria.uppercaseLetter
                      ? 'alert_success'
                      : 'alert_error'
                }
              >
                {t('watermelon-UPPERCASE_LETTER')}
              </Text>
            </ZoneArea>

            <ZoneArea area="lowercase_letter">
              <Text
                whiteSpace="nowrap"
                variant="caption"
                color={
                  !touched
                    ? 'black_50'
                    : passwordCriteria.lowercaseLetter
                      ? 'alert_success'
                      : 'alert_error'
                }
              >
                {t('watermelon-LOWERCASE_LETTER')}
              </Text>
            </ZoneArea>

            <ZoneArea area="digit">
              <Text
                whiteSpace="nowrap"
                variant="caption"
                color={
                  !touched
                    ? 'black_50'
                    : passwordCriteria.digit
                      ? 'alert_success'
                      : 'alert_error'
                }
              >
                {t('watermelon-DIGIT')}
              </Text>
            </ZoneArea>

            <ZoneArea area="special_character">
              <Text
                whiteSpace="nowrap"
                variant="caption"
                color={
                  !touched
                    ? 'black_50'
                    : passwordCriteria.specialCharacter
                      ? 'alert_success'
                      : 'alert_error'
                }
              >
                {t('watermelon-SPECIAL_CHARACTER').replace(
                  '[CHARACTERS]',
                  SPECIAL_CHARACTERS,
                )}
              </Text>
            </ZoneArea>
          </Zone>
        )}
      </Col>
    );
  },
);
